import { useMemo, useState } from 'react'
import ReportControls, { type ReportFilter } from './ReportControls'
import ReportTable, { type ReportTableColumn } from './ReportTable'
import {
  DETAIL_STATUS_LABEL,
  buildDetailedOutRows,
  filterDetailedOutRows,
  totalPersonDays,
  type DetailStatus,
  type DetailedOutFilters,
  type DetailedOutRow,
} from './detailedOutFigures'
import { getDetailRecords } from './detailedOutStore'
import { getTeamLookups } from './coverageReportStore'
import ExportBar from './ExportBar'
import { detailedOutExportTable } from './peopleReportExport'
import { fullDate, todayIso } from './reportDates'
import { nextSort, sortRows, type ReportSort, type SortValue } from './reportSort'
import { supervisorLabel } from './supervisorLabel'
import { useLoaded } from './useLoaded'
import { useReportRange } from './useReportRange'

/**
 * docs/product/reports.md's detailed-out report: each detail touching the
 * date range, with its dates, its status today, and what it takes out of
 * available manpower.
 */
const COLUMNS: ReportTableColumn<DetailedOutRow>[] = [
  { id: 'employee', label: 'Employee', cell: (row) => row.employee },
  { id: 'detailType', label: 'Detail type', cell: (row) => row.detailType },
  { id: 'team', label: 'Originating team', cell: (row) => row.team },
  { id: 'destination', label: 'Destination', cell: (row) => row.destination },
  { id: 'startDate', label: 'Start date', cell: (row) => fullDate(row.startDate) },
  { id: 'endDate', label: 'End date', cell: (row) => fullDate(row.endDate) },
  { id: 'status', label: 'Status', cell: (row) => DETAIL_STATUS_LABEL[row.status] },
  { id: 'daysInRange', label: 'Days in range', numeric: true, cell: (row) => row.daysInRange },
  { id: 'effect', label: 'Effect on available manpower', cell: (row) => row.effect },
]

const SORT_VALUE: Record<string, (row: DetailedOutRow) => SortValue> = {
  employee: (row) => row.employee,
  detailType: (row) => row.detailType,
  team: (row) => row.team,
  destination: (row) => row.destination,
  startDate: (row) => row.startDate,
  endDate: (row) => row.endDate,
  status: (row) => row.status,
  daysInRange: (row) => row.daysInRange,
  effect: (row) => row.daysInRange,
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-card border border-line bg-canvas p-4">
      <p className="text-caption font-bold tracking-wide text-ink-subtle uppercase">{label}</p>
      <p className="mt-1 text-h2 font-extrabold [font-variant-numeric:tabular-nums]">{value}</p>
    </div>
  )
}

export default function DetailedOutReport() {
  const range = useReportRange()
  const { from, to } = range
  const [filters, setFilters] = useState<DetailedOutFilters>({})
  const [sort, setSort] = useState<ReportSort>({ column: 'startDate', direction: 'asc' })
  const { data, loading, failed } = useLoaded(
    async () => {
      const [records, lookups] = await Promise.all([getDetailRecords(), getTeamLookups()])
      return { records, lookups }
    },
    { records: [], lookups: { teams: new Map<string, string>(), supervisors: new Map<string, string>() } },
  )
  const { lookups } = data
  const today = todayIso()

  const allRows = useMemo(() => buildDetailedOutRows(data.records, { from, to, today }, lookups.supervisors), [data.records, from, to, today, lookups])
  const filtered = useMemo(() => filterDetailedOutRows(allRows, filters), [allRows, filters])
  const rows = useMemo(() => sortRows(filtered, SORT_VALUE[sort.column], sort.direction), [filtered, sort])
  const personDays = totalPersonDays(filtered)

  const reportFilters: ReportFilter[] = [
    {
      id: 'team',
      label: 'Team',
      allLabel: 'All teams',
      value: filters.teamId ?? '',
      options: Array.from(new Set(allRows.map((row) => row.teamId))).map((id) => ({ value: id, label: lookups.teams.get(id) ?? 'Unknown team' })),
      onChange: (value) => setFilters((current) => ({ ...current, teamId: value || undefined })),
    },
    {
      id: 'supervisor',
      label: 'Supervisor',
      allLabel: 'All supervisors',
      value: filters.supervisorId ?? '',
      options: Array.from(new Set(allRows.flatMap((row) => (row.supervisorId ? [row.supervisorId] : [])))).map((id) => ({
        value: id,
        label: supervisorLabel(id, lookups),
      })),
      onChange: (value) => setFilters((current) => ({ ...current, supervisorId: value || undefined })),
    },
    {
      id: 'status',
      label: 'Status',
      allLabel: 'All statuses',
      value: filters.status ?? '',
      options: (Object.keys(DETAIL_STATUS_LABEL) as DetailStatus[]).map((status) => ({ value: status, label: DETAIL_STATUS_LABEL[status] })),
      onChange: (value) => setFilters((current) => ({ ...current, status: (value || undefined) as DetailStatus | undefined })),
    },
  ]

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Totals" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Details in range" value={filtered.length} />
        <Tile label="Active today" value={filtered.filter((row) => row.status === 'active').length} />
        <Tile label="Upcoming" value={filtered.filter((row) => row.status === 'upcoming').length} />
        <Tile label="Person-days detailed out" value={personDays} />
      </section>

      <ReportControls
        range={range}
        filters={reportFilters}
        search={{ value: filters.search ?? '', onChange: (value) => setFilters((current) => ({ ...current, search: value || undefined })) }}
        onReset={() => setFilters({})}
      />

      <ExportBar
        count={`${rows.length} ${rows.length === 1 ? 'detail' : 'details'}`}
        loading={loading}
        disabled={loading || failed || range.invalid || rows.length === 0}
        fileStem={`muster-detailed-out-${from}-to-${to}`}
        table={() => detailedOutExportTable(rows, personDays, { from, to, filters, lookups, generatedAt: new Date() })}
      />

      {failed ? (
        <p role="alert" className="rounded-card border border-line bg-canvas p-4 text-small text-error">
          The detailed-out report could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : (
        <ReportTable
          label="Detailed out"
          caption="Details touching the date range, with their dates, status, and effect on available manpower"
          columns={COLUMNS}
          rows={rows}
          rowKey={(row) => row.key}
          sort={sort}
          onSort={(column) => setSort((current) => nextSort(current, column))}
          loading={loading}
          empty="No details in these dates. Choose another pay period or clear a filter."
          minWidth={1100}
        />
      )}
    </div>
  )
}
